import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {AgendaEventTeamMemberSelection} from '@/features/agenda/components/agenda-event-form/agenda-event-team-member-selection/AgendaEventTeamMemberSelection'
import {StyledTeamMemberGridItem} from '@/features/agenda/components/agenda-event-form/agenda-event-team-members-selector/style'
import {AgendaEventFieldsSchema} from '@/features/agenda/formSchemas'
import {InputSearchTeamMember} from '@/features/treatment-requests/teams/components/input-search-team-member/InputSearchTeamMember'
import {useGetTeamMembers} from '@/features/treatment-requests/teams/queries/useGetTeamMembers'
import {useTranslation} from '@/translations/i18n'
import {useMemo} from 'react'
import {useController, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const AgendaEventTeamMembersSelectorSchema = AgendaEventFieldsSchema.pick({
    patients: true,
    eventTeamMembers: true,
    consultants: true
})
type AgendaEventTeamMembersSelectorSchema = z.infer<typeof AgendaEventTeamMembersSelectorSchema>

export const AgendaEventTeamMembersSelector = () => {
    const {t} = useTranslation()
    const {control} = useFormContext<AgendaEventTeamMembersSelectorSchema>()
    const {field} = useController({control, name: 'eventTeamMembers'})
    const patients = useWatch({control, name: 'patients'})
    const treatmentRequestId = useMemo(
        () => patients?.[0]?.project?.treatmentRequestId,
        [patients?.[0]?.project?.treatmentRequestId]
    )
    const consultants = useWatch({control, name: 'consultants'})

    const {isLoading: isLoadingTeamMembers} = useGetTeamMembers(
        {
            urlParams: {
                treatmentRequestId: treatmentRequestId || ''
            },
            params: {
                limit: 100,
                responseType: 'small',
                projectId: patients.length == 1 ? patients?.[0]?.project?.id : undefined,
                page: 1
            }
        },
        {enabled: !!treatmentRequestId}
    )

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            <Grid columns={2} gap={4} fullWidth>
                <GridItem spanColumn={1}>
                    <InputSearchTeamMember
                        {...field}
                        label={t('agenda:participants')}
                        placeholder={t('agenda:add_participants')}
                        projectId={patients.length == 1 ? patients?.[0]?.project?.id : undefined}
                        treatmentRequestId={treatmentRequestId || ''}
                        disabled={!treatmentRequestId || isLoadingTeamMembers}
                        disabledConsultants={consultants?.map(consultant => consultant.id)}
                    />
                </GridItem>
            </Grid>
            <Grid columns={2} gap={4} fullWidth>
                {field.value?.map(value => (
                    <StyledTeamMemberGridItem key={value.id} spanColumn={1}>
                        <AgendaEventTeamMemberSelection
                            teamMember={value}
                            onRemove={() =>
                                field.onChange(field.value?.filter(teamMember => teamMember.id != value.id))
                            }
                        />
                    </StyledTeamMemberGridItem>
                ))}
            </Grid>
        </Flexbox>
    )
}
